import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BusinessIcon from "@mui/icons-material/Business";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import { Link, useHistory, useLocation } from "react-router-dom";

import {
  clearNetworkPortalToken,
  getCurrentNetworkPortalMember,
  getNetworkPortalToken,
  logoutNetworkPortalMember,
} from "../../services/coreNetworkPortal";
import "../talent/TalentPortalLayout.css";

export const TALENT_PLACEHOLDER_AVATAR = "/elite-core-placeholder-avatar.png";

export function toTitleCase(value) {
  const source = String(value || "").replace(/[_-]+/g, " ").trim();
  if (!source) return "-";
  return source.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatDateTime(value, fallback = "-") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString();
}

export function formatCurrency(value, currency = "CAD") {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency || "CAD",
    maximumFractionDigits: 2,
  }).format(numeric);
}

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function getTalentProfileImageUrl(member) {
  return member?.profileImageUrl || TALENT_PLACEHOLDER_AVATAR;
}

const statusColorMap = {
  pending: "warning",
  sent: "secondary",
  viewed: "secondary",
  accepted: "primary",
  active: "success",
  assigned: "primary",
  in_progress: "primary",
  submitted: "info",
  revision_requested: "warning",
  ready: "success",
  completed: "success",
  approved: "success",
  verified: "success",
  declined: "error",
  rejected: "error",
  expired: "default",
  cancelled: "default",
  disabled: "default",
};

export function StatusChip({ value }) {
  const normalized = String(value || "").toLowerCase();
  return <Chip size="small" label={toTitleCase(value)} color={statusColorMap[normalized] || "default"} />;
}

const portalNavItems = [
  { label: "Dashboard", caption: "Overview", path: "/talent", exact: true, icon: DashboardIcon },
  { label: "Assignments", caption: "Assigned work", path: "/talent/assignments", icon: AssignmentTurnedInIcon },
  { label: "Offers", caption: "Available work", path: "/talent/offers", icon: LocalOfferIcon },
  { label: "Profile", caption: "Account details", path: "/talent/profile", icon: PersonIcon },
];

function isNavActive(pathname, item) {
  if (item.exact) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function TalentPortalSidebar({ member, collapsed, onToggleCollapsed, onNavigate, onSignOut, mobile = false }) {
  const location = useLocation();

  return (
    <div className="talent-portal__sidebar-inner sidebar">
      <div className="talent-portal__sidebar-header">
        <span className="talent-portal__brand-mark" aria-hidden="true">EC</span>
        <span className="talent-portal__brand-copy">
          <span className="talent-portal__brand-title">Elite Core</span>
          <span className="talent-portal__brand-subtitle">Talent Portal</span>
        </span>
        {mobile ? (
          <IconButton className="talent-portal__collapse-button" onClick={onNavigate} aria-label="Close Talent Portal navigation">
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton className="talent-portal__collapse-button" onClick={onToggleCollapsed} aria-label={collapsed ? "Expand Talent Portal sidebar" : "Collapse Talent Portal sidebar"}>
            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        )}
      </div>

      <nav className="talent-portal__nav" aria-label="Talent Portal navigation">
        <span className="talent-portal__sidebar-section-label">Workspace</span>
        {portalNavItems.map((item) => {
          const Icon = item.icon;
          const active = isNavActive(location.pathname, item);
          const button = (
            <Button
              key={item.path}
              component={Link}
              to={item.path}
              onClick={onNavigate}
              className={`talent-portal__nav-item${active ? " talent-portal__nav-item--active" : ""}`}
              aria-current={active ? "page" : undefined}
            >
              <span className="talent-portal__nav-icon"><Icon fontSize="small" /></span>
              <span className="talent-portal__nav-text">
                <span className="talent-portal__nav-label">{item.label}</span>
                <span className="talent-portal__nav-caption">{item.caption}</span>
              </span>
            </Button>
          );
          return collapsed && !mobile ? <Tooltip key={item.path} title={item.label} placement="right">{button}</Tooltip> : button;
        })}
      </nav>

      <div className="talent-portal__sidebar-footer">
        {member ? (
          <div className="talent-portal__member-mini">
            <Avatar src={getTalentProfileImageUrl(member)} alt={member.displayName || "Talent profile"} sx={{ width: 34, height: 34 }} />
            <span className="talent-portal__member-block">
              <span className="talent-portal__member-name">{member.displayName || "Talent member"}</span>
              <span className="talent-portal__member-meta">{member.companyName || member.email || "Core Network"}</span>
            </span>
          </div>
        ) : null}
        <Button className="talent-portal__nav-item talent-portal__sign-out" onClick={onSignOut}>
          <span className="talent-portal__nav-icon"><LogoutIcon fontSize="small" /></span>
          <span className="talent-portal__nav-text"><span className="talent-portal__nav-label">Sign out</span></span>
        </Button>
      </div>
    </div>
  );
}

export function TalentPortalCard({ title, subtitle, actions, children, className = "" }) {
  return (
    <section className={`talent-portal__card${className ? ` ${className}` : ""}`}>
      {title || subtitle || actions ? (
        <header className="talent-portal__card-header">
          <div>
            {title ? <h2 className="talent-portal__card-title">{title}</h2> : null}
            {subtitle ? <p className="talent-portal__card-subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="talent-portal__header-actions">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function TalentPortalEmptyState({ icon, title, description, action }) {
  return (
    <div className="talent-portal__empty">
      <span className="talent-portal__empty-icon" aria-hidden="true">{icon || <BusinessIcon fontSize="small" />}</span>
      <div>
        <p className="talent-portal__empty-title">{title}</p>
        {description ? <p className="talent-portal__empty-description">{description}</p> : null}
        {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
      </div>
    </div>
  );
}

function TalentPortalPageHeader({ title, subtitle, member, actions }) {
  const memberships = safeArray(member?.memberships);
  const primaryMembership = memberships[0];
  const memberMeta = [
    member?.email,
    member?.companyName || primaryMembership?.originatingCompanyDisplayName,
    member?.memberType ? toTitleCase(member.memberType) : null,
  ].filter(Boolean).join(" • ");

  return (
    <header className="talent-portal__page-header">
      <div className="talent-portal__page-heading">
        <Avatar className="talent-portal__page-avatar" src={getTalentProfileImageUrl(member)} alt={member?.displayName || "Talent profile"} />
        <div>
          <p className="talent-portal__eyebrow">Talent Portal</p>
          <h1 className="talent-portal__title">{title}</h1>
          {subtitle ? <p className="talent-portal__subtitle">{subtitle}</p> : null}
          {member ? <p className="talent-portal__meta">Signed in as {member.displayName || "Talent member"}{memberMeta ? ` • ${memberMeta}` : ""}</p> : null}
        </div>
      </div>
      {actions ? <div className="talent-portal__header-actions">{actions}</div> : null}
    </header>
  );
}

export function NetworkPortalLayout({ title, subtitle, member, actions, children }) {
  const history = useHistory();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("talentPortalSidebarCollapsed") === "true");

  const handleToggleCollapsed = () => {
    setCollapsed((current) => {
      localStorage.setItem("talentPortalSidebarCollapsed", String(!current));
      return !current;
    });
  };

  const handleSignOut = async () => {
    try {
      await logoutNetworkPortalMember();
    } catch (_error) {
      // Ignore logout failures for local token cleanup.
    }
    clearNetworkPortalToken();
    history.replace("/talent/login");
  };

  const sidebar = (
    <TalentPortalSidebar
      member={member}
      collapsed={collapsed}
      onToggleCollapsed={handleToggleCollapsed}
      onNavigate={() => setMobileOpen(false)}
      onSignOut={handleSignOut}
    />
  );

  return (
    <div className={`talent-portal instance-dashboard${collapsed ? " talent-portal--collapsed" : ""}`}>
      <aside className={`talent-portal__sidebar leftSide${collapsed ? " sidebar-collapsed" : ""}`}>
        <div className="talent-portal__sidebar-nav ec-instance-core-nav">
          {sidebar}
        </div>
      </aside>
      <main className="talent-portal__main">
        <div className="talent-portal__content">
          <div className="talent-portal__mobile-bar">
            <IconButton className="talent-portal__mobile-menu-button" onClick={() => setMobileOpen(true)} aria-label="Open Talent Portal navigation">
              <MenuIcon fontSize="small" />
            </IconButton>
            <span className="talent-portal__mobile-title">Elite Core Talent Portal</span>
            <Avatar src={getTalentProfileImageUrl(member)} alt={member?.displayName || "Talent profile"} sx={{ width: 34, height: 34 }} />
          </div>
          <TalentPortalPageHeader title={title} subtitle={subtitle} member={member} actions={actions} />
          {children}
        </div>
      </main>
      <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ className: "talent-portal__drawer-paper" }}>
        <div className="talent-portal" style={{ minHeight: "100%" }}>
          <TalentPortalSidebar member={member} collapsed={false} mobile onNavigate={() => setMobileOpen(false)} onSignOut={handleSignOut} />
        </div>
      </Drawer>
    </div>
  );
}

export function useNetworkPortalSession({ disabled = false } = {}) {
  const location = useLocation();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Administrator previews render the Talent view without using Talent auth.
    // Never bootstrap a Talent session (or redirect) in read-only preview mode.
    if (disabled) {
      setLoading(false);
      return undefined;
    }
    let active = true;

    async function bootstrap() {
      try {
        setLoading(true);
        setError("");

        const query = new URLSearchParams(location.search);
        const inviteToken = query.get("token");

        if (inviteToken) {
          window.location.replace(`/talent/verify-account?token=${encodeURIComponent(inviteToken)}`);
          return;
        }

        const token = getNetworkPortalToken();
        if (!token) {
          if (active) {
            setMember(null);
            const returnTo = `${location.pathname}${location.search}${location.hash || ""}`;
            window.location.replace(`/talent/login?returnTo=${encodeURIComponent(returnTo)}`);
          }
          return;
        }

        const response = await getCurrentNetworkPortalMember();
        if (active) {
          setMember(response.member || null);
        }
      } catch (sessionError) {
        const expired = [401, 403].includes(sessionError?.response?.status);
        if (expired) clearNetworkPortalToken();
        if (active) {
          setMember(null);
          if (expired) {
            const returnTo = `${location.pathname}${location.search}${location.hash || ""}`;
            window.location.replace(`/talent/login?returnTo=${encodeURIComponent(returnTo)}`);
          } else {
            // Transient connectivity or server errors should not log Talent out.
            setError(sessionError?.response?.data?.message || sessionError.message || "Unable to load your portal session.");
          }
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    bootstrap();
    return () => {
      active = false;
    };
  }, [disabled, location.pathname, location.search, location.hash]);

  return { member, loading, error, setError };
}

export function PortalLoading({ label = "Loading portal..." }) {
  return (
    <div className="talent-portal-loading-state">
      <div className="talent-portal__state-card">
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </div>
    </div>
  );
}

export function PortalAuthState({ error }) {
  return (
    <div className="talent-portal-auth-state">
      <TalentPortalCard title="Welcome" subtitle="Global Talent access">
        <Stack spacing={3}>
          <Alert severity={error ? "error" : "info"}>
            {error || "Log in with your Talent portal credentials to access your assigned work."}
          </Alert>
          <Divider />
          <Typography variant="body2" color="text.secondary">
            This portal is limited to work offers and assignments that have been specifically shared with your Core Network Talent profile.
          </Typography>
          <Button component={Link} to="/talent/login" variant="contained">Go to Talent Login</Button>
        </Stack>
      </TalentPortalCard>
    </div>
  );
}
